<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import MxIcon from './MxIcon.vue';
import type { Template } from '@/lib/templates';

const props = defineProps<{ template: Template; aspect?: '9 / 16' | '1 / 1' }>();
defineEmits<{ (e: 'select', t: Template): void }>();

const isVideo = props.template.examples[0]?.url.endsWith('.mp4');
const previewUrl = props.template.examples[0]?.url ?? '';
const posterUrl = props.template.examples[0]?.poster ?? '';

// Авто-плей видео только когда карточка в зоне видимости. Раньше каждое
// `<video autoplay>` дёргало сеть на старте — особенно больно на iOS.
const videoEl = ref<HTMLVideoElement | null>(null);
let observer: IntersectionObserver | null = null;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

onMounted(() => {
  if (!isVideo || !videoEl.value) return;
  if (prefersReducedMotion()) return;
  if (typeof IntersectionObserver === 'undefined') {
    // Старый WebView без IO — оставим poster без автоплея, не падаем.
    return;
  }
  observer = new IntersectionObserver(
    (entries) => {
      const v = videoEl.value;
      if (!v) return;
      for (const e of entries) {
        if (e.isIntersecting) {
          void v.play().catch(() => { /* WebView иногда дропает */ });
        } else {
          v.pause();
        }
      }
    },
    { threshold: 0.5 },
  );
  observer.observe(videoEl.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});
</script>

<template>
  <button class="tc" :style="{ aspectRatio: aspect ?? '9 / 16' }" @click="$emit('select', template)">
    <video
      v-if="isVideo"
      ref="videoEl"
      class="tc__media"
      :src="previewUrl"
      :poster="posterUrl"
      muted
      loop
      playsinline
      preload="metadata"
    />
    <img v-else class="tc__media" :src="previewUrl" :alt="template.title" loading="lazy" />

    <span class="tc__shade" />

    <span v-if="template.badge" :class="['tc__badge', `tc__badge--${template.badge}`]">
      {{ template.badge === 'popular' ? 'Популярное' : 'Новинка' }}
    </span>
    <span class="tc__kind">
      <MxIcon :name="template.kind === 'video' ? 'Film' : 'Image'" :size="14" />
    </span>
    <span class="tc__title">{{ template.title }}</span>
  </button>
</template>

<style scoped>
.tc {
  position: relative;
  display: block;
  border-radius: var(--r-lg);
  overflow: hidden;
  background: var(--bg-elevated);
  border: 1px solid var(--hairline);
  width: 100%;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease, border-color 0.18s ease;
}
.tc:active { transform: scale(0.98); }
.tc__media { width: 100%; height: 100%; object-fit: cover; display: block; }
.tc__shade {
  position: absolute;
  inset: auto 0 0 0;
  height: 45%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.78) 0%, rgba(0, 0, 0, 0.35) 50%, transparent 100%);
  pointer-events: none;
}
.tc__title {
  position: absolute;
  left: 12px;
  bottom: 12px;
  right: 36px;
  color: #fff;
  font-weight: 700;
  font-size: var(--fs-sm);
  letter-spacing: 0.02em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
  text-align: left;
  text-transform: uppercase;
}
.tc__badge {
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: var(--fs-xs);
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--accent);
  color: #000;
}
.tc__kind {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  backdrop-filter: blur(6px);
}
</style>
